import type { FC } from 'react';
import React, { memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { Subscribe } from 'unstated';
import { useQuery } from '@apollo/react-hooks';

import { Flex, xcss } from '@atlaskit/primitives';

import { expVal } from '@confluence/feature-experiments';
import {
	EditorContentStateQuery,
	type EditorContentStateQueryType,
	type EditorContentStateQueryVariables,
	ContentStateStateContainer,
	EditorContentState,
	RendererContentState,
} from '@confluence/content-state';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { isExternalShareRequest } from '@confluence/external-share-utils';
import { fg } from '@confluence/feature-gating';
import { PageOwnershipByline, BadgeStylePageOwnershipByline } from '@confluence/content-ownership';
import { ProfileAvatar, ProfileLink } from '@confluence/profile';
import { useSpaceId } from '@confluence/space-utils';
import { LivePagesByLineReactions } from '@confluence/live-pages-features/entry-points/LivePagesByLineReactions';
import { AnalyticsByLine } from '@confluence/confluence-analytics';
import { ReadTime } from '@confluence/read-time';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';

import { useByLineApps } from './useByLineApps';
import { LazyLivePagesByLineApps } from './LivePagesByLineApps';
import { png_1x1_n40 } from './png1x1';
import {
	BylineStyledTextDiv,
	StyledAuthorDiv,
	StyledAvatars,
	LivePagesBylineStyledMainDiv,
	StyledNameSkeleton,
} from './PresentationalComponents';
import type { UserShape, FullPageEditorPresetAPI, Version } from './byLineTypes';
import { getArrangedContributors } from './byLineUtils';
import { LivePagesByLineLastUpdated } from './LivePagesByLineLastUpdated';
import { BylineClassification } from './BylineClassification';
import { SingleLineByLine } from './SingleLineByLine';

export type LivePagesByLineWithContributorProps = {
	contentId: string;
	author: UserShape;
	owner: UserShape | null;
	lastOwner: UserShape | null;
	createdDate: Date | null;
	contributors: UserShape[];
	contributorsCount: number;
	isOwnerContributor: boolean;
	lastModifyUser: UserShape;
	contentType: string;
	isPublishingContent: boolean;
	userOffline: boolean;
	isKnowledgeBaseSpace: boolean;
	version: Version;
	viewMode?: boolean;
	editorApi?: FullPageEditorPresetAPI | null;
	isUnpublishedDraft?: boolean;
};

type userAvatarShape = {
	isAnonymous: boolean;
	userId: string;
	src: string;
	name: string;
};

const bylineAppsStyles = xcss({
	marginTop: 'space.100',
	font: 'font.body.small',
	display: 'inline',
});

export const LivePagesByLineWithContributorsComponent: FC<LivePagesByLineWithContributorProps> =
	memo(
		({
			contentId,
			author,
			owner,
			createdDate,
			lastModifyUser,
			contributors,
			contributorsCount,
			isOwnerContributor,
			lastOwner,
			contentType,
			isPublishingContent,
			userOffline,
			isKnowledgeBaseSpace,
			viewMode,
			editorApi,
			isUnpublishedDraft,
		}) => {
			const isLivePage = useIsCurrentPageLive();
			const isExternalShare = isExternalShareRequest();
			const spaceId = useSpaceId();
			const { hasApps, connectItems, forgeItems, forgeError } = useByLineApps(contentId);

			const { data } = useQuery<EditorContentStateQueryType, EditorContentStateQueryVariables>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
				EditorContentStateQuery,
				{
					fetchPolicy: 'cache-first',
					variables: {
						contentId,
						isDraft: true,
					},
					skip: !Boolean(window.__SSR_RENDERED__),
				},
			);

			const getContributorsData = useMemo(() => {
				const arrangedContributors: UserShape[] = getArrangedContributors({
					owner,
					author,
					lastModifier: lastModifyUser,
					contributors,
				});

				const userToAvatarGroupDataItem: (user) => userAvatarShape = (user) => ({
					// Props for <ProfileAvatar>
					isAnonymous: user.isAnonymous,
					userId: user.userId,
					src: user.avatarUrl,
					// Extra props
					name: user.fullName,
				});

				return [
					...arrangedContributors
						// Actual data for the users we know about
						.map(userToAvatarGroupDataItem),
					// Skeletons for the ones we don't
					...Array(Math.max(contributorsCount - arrangedContributors.length, 0))
						.fill(null)
						.map(() => ({
							src: png_1x1_n40,
							name: <StyledNameSkeleton />,
						})),
				];
			}, [owner, author, lastModifyUser, contributors, contributorsCount]);

			const pageOwnerOrFirstContributorData = getContributorsData[0] as userAvatarShape;
			const contributorCountWithoutOwner = contributorsCount - (isOwnerContributor ? 1 : 0);

			const CreatorOrOwnerByline = useMemo(() => {
				if (owner !== null) {
					return (
						<PageOwnershipByline
							owner={owner}
							creator={author}
							createdDate={createdDate}
							templateId={null}
							contentId={contentId}
							lastOwner={lastOwner}
						/>
					);
				}

				return (
					<FormattedMessage
						id="content-header.livepages.by-line.by.expanded"
						defaultMessage="By {author}"
						description="Text to show author details"
						values={{
							author: (
								<ProfileLink
									userId={author.userId}
									fullName={author.fullName}
									isAnonymous={author.isAnonymous}
									referralSource="profilecard"
								/>
							),
						}}
					/>
				);
			}, [author, owner, createdDate, contentId, lastOwner]);

			const singleLineOwnershipByline = !fg('cc_page_experiences_byline_badge_style') ? (
				<Flex alignItems="center" gap="space.100">
					<ProfileAvatar
						src={pageOwnerOrFirstContributorData.src}
						userId={pageOwnerOrFirstContributorData.userId}
						isAnonymous={pageOwnerOrFirstContributorData.isAnonymous}
						name={pageOwnerOrFirstContributorData.name}
						size="small"
						testId="live-pages-byline-owner-or-first-contributor-avatar"
					/>
					<StyledAuthorDiv>{CreatorOrOwnerByline}</StyledAuthorDiv>
				</Flex>
			) : (
				<BadgeStylePageOwnershipByline
					owner={owner}
					author={author}
					analyticsSource="liveDoc"
					contributorCountWithoutOwner={contributorCountWithoutOwner}
				/>
			);

			const ByLineCommonContent = (
				<>
					{fg('cc_page_experiences_byline_classification') && (
						<BylineClassification contentId={contentId} />
					)}
					<Subscribe to={[ContentStateStateContainer]}>
						{(contentStateState: ContentStateStateContainer) => {
							// view mode users should not be able to edit the content state, so we just render the content state.
							if (viewMode) {
								return (
									<RendererContentState
										contentId={contentId}
										hasSeparator={false}
										isDraft={!isLivePage}
									/>
								);
							}
							// Knowledge space live pages will always show the editable content state component.
							// Other live pages will only show the editable content state component if the content state is already set.
							// This is because a page without a content state is initialized by the ContentStateHoverButton component.
							// SSRd content state will have it's value in gql cache initially instead of the unstated store
							const isSSRRenderedOnLoad =
								Boolean(data?.singleContent?.contentState) &&
								Boolean(window.__SSR_RENDERED__) &&
								contentStateState.state.shouldRunContentStateEffect;
							if (
								contentStateState.state.contentState ||
								isKnowledgeBaseSpace ||
								isSSRRenderedOnLoad
							) {
								return (
									<EditorContentState
										contentId={contentId}
										contentType={contentType}
										isPublishingContent={isPublishingContent}
										userOffline={userOffline}
										floatingToolbarButtonStyle={{
											alignItems: 'center',
											height: '24px',
										}}
									/>
								);
							} else {
								return null;
							}
						}}
					</Subscribe>
					{
						// eslint-disable-next-line confluence-feature-gating/no-preconditioning
						isLivePage &&
							fg('live_pages_byline_last_updated') &&
							!fg('confluence_frontend_object_header') && (
								<LivePagesByLineLastUpdated contentId={contentId} editorApi={editorApi} />
							)
					}
					{fg('live_pages_byline_read_time') && (
						<ReadTime contentId={contentId} editorApi={editorApi} isInEditor />
					)}
					{(isLivePage || !isUnpublishedDraft) && (
						<AnalyticsByLine contentId={contentId} contentAuthorId={author.userId} />
					)}
					{(isLivePage || !isUnpublishedDraft) && (
						<LivePagesByLineReactions
							contentId={contentId}
							spaceId={spaceId || ''}
							isDisabled={
								expVal('platform_editor_offline_editing_web', 'isEnabled', false) && userOffline
							}
							isContentCreationPanelOpen
							isSubtle
							contentType={contentType}
						/>
					)}
				</>
			);

			if (fg('confluence_frontend_single_line_byline_live_pages')) {
				return (
					<SingleLineByLine contentId={contentId} contentType={contentType as 'page' | 'blogpost'}>
						{singleLineOwnershipByline}
						{ByLineCommonContent}
					</SingleLineByLine>
				);
			}

			return (
				<Flex direction="column" testId="live-pages-byline-multi-line">
					<LivePagesBylineStyledMainDiv
						hasFixedHeight={fg('cc_page_experiences_live_pages_byline_placeholder')}
					>
						{!fg('cc_page_experiences_byline_badge_style') && (
							<StyledAvatars>
								<ProfileAvatar
									src={pageOwnerOrFirstContributorData.src}
									userId={pageOwnerOrFirstContributorData.userId}
									isAnonymous={pageOwnerOrFirstContributorData.isAnonymous}
									name={pageOwnerOrFirstContributorData.name}
									size="small"
									testId="live-pages-byline-owner-or-first-contributor-avatar"
								/>
							</StyledAvatars>
						)}
						<BylineStyledTextDiv
							data-testId="byline-text-component"
							hasBadgeStylePageOwnerBylineItem={fg('cc_page_experiences_byline_badge_style')}
						>
							<Flex gap="space.075" justifyContent="center" alignItems="center">
								{!fg('cc_page_experiences_byline_badge_style') ? (
									<StyledAuthorDiv>{CreatorOrOwnerByline}</StyledAuthorDiv>
								) : (
									<BadgeStylePageOwnershipByline
										owner={owner}
										author={author}
										analyticsSource={isLivePage ? 'liveDoc' : 'editor'}
										contributorCountWithoutOwner={contributorCountWithoutOwner}
									/>
								)}
								{ByLineCommonContent}
							</Flex>
						</BylineStyledTextDiv>
					</LivePagesBylineStyledMainDiv>
					{!isExternalShare && hasApps && (
						<Flex alignItems="center" xcss={bylineAppsStyles}>
							<ErrorBoundary attribution={Attribution.ECOSYSTEM}>
								<LazyLivePagesByLineApps
									contentId={contentId}
									connectItems={connectItems}
									forgeItems={forgeItems}
									forgeError={forgeError}
									showLivePagesExtensionPoints
								/>
							</ErrorBoundary>
						</Flex>
					)}
				</Flex>
			);
		},
	);
